import { FC, useCallback, useState } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Button } from 'src/general/components/Button/Button';
import { PdpDetailsBlock } from 'src/pdp/components/PdpDetailsBlock/PdpDetailsBlock';
import { getMicrocopy } from 'src/stores/microcopyStore';

import styles from './CarFeatures.module.scss';

type Props = {
  isOnlyShowFeatureData?: boolean;
  isShowAllData?: boolean;
} & PDPDetailsProps;

interface PDPDetailsProps {
  optionalFeatures: string[];
  standardFeatures: string[];
  aftermarketFeatures: string[];
}

const NUMBER_ROWS = 6;

interface FeatureGroup {
  microcopyKey: MICROCOPY;
  features: keyof PDPDetailsProps;
}

const featuresData: FeatureGroup[] = [
  {
    microcopyKey: MICROCOPY.PDP_CAR_FEATURES_ITEM_1_HEADER,
    features: 'optionalFeatures',
  },
  {
    microcopyKey: MICROCOPY.PDP_CAR_FEATURES_ITEM_2_HEADER,
    features: 'aftermarketFeatures',
  },
  {
    microcopyKey: MICROCOPY.PDP_CAR_FEATURES_ITEM_3_HEADER,
    features: 'standardFeatures',
  },
];

export const CarFeatures: FC<Props> = (props) => {
  const [showAll, setShowAll] = useState<boolean>(false);
  const hasMore = props.standardFeatures.length > NUMBER_ROWS;
  const showAllLabel = getMicrocopy(MICROCOPY.PDP_CAR_FEATURES_BUTTON_SHOW_ALL_LABEL);
  const onShowAll = useCallback(() => {
    setShowAll(true);
  }, [setShowAll]);

  return (
    <section className={styles.root}>
      {!props.isOnlyShowFeatureData && (
        <h3 className={styles.header}>{getMicrocopy(MICROCOPY.PDP_CAR_FEATURES_HEADER)}</h3>
      )}
      <div className={styles.carFeaturesContainer}>
        {featuresData.map(({ microcopyKey, features }) => (
          <PdpDetailsBlock
            key={microcopyKey}
            header={getMicrocopy(microcopyKey)}
            items={props[features]
              .filter(
                (_value, index) =>
                  props.isShowAllData ||
                  showAll ||
                  features === 'optionalFeatures' ||
                  features === 'aftermarketFeatures' ||
                  (features === 'standardFeatures' && index < NUMBER_ROWS),
              )
              .map((feature) => ({
                key: feature,
                title: feature,
                value: null,
              }))}
          />
        ))}
        {props.isShowAllData ||
          (!showAll && hasMore && !props.isOnlyShowFeatureData && (
            <div className={styles.fadeOutContainer}>
              <Button
                variant="secondary"
                size="small"
                onClick={onShowAll}
                data-testid={MICROCOPY.PDP_CAR_FEATURES_BUTTON_SHOW_ALL_LABEL}
              >
                {showAllLabel}
              </Button>
            </div>
          ))}
      </div>
    </section>
  );
};
